import React, { forwardRef, useRef, useCallback, useEffect } from "react";
import styled from "styled-components";
import Colors from "../../../ui/global/Colors";
import { tools } from "./ToolbarDraw";
import KonvaCanvas, { KonvaCanvasProps, KonvaArtStageRefs } from "./KonvaCanvas";
import { TransformState } from "../utils/useArtHistory";
import LoadingSpinner from "../../../ui/components/LoadingSpinner";
import ArtDescription, { ArtDescriptionProps } from "./ArtDescription";
import { ArtType } from "../Art";
import Stage3D from "./Stage3D";
import { Phases3D } from "../utils/useAI";
import { motion, AnimatePresence } from "framer-motion";
import Mini3DView from "./Mini3DView";

const StageRoot = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    width: 100%;
`;

const Inner = styled.div<{ $orientation: 'horizontal' | 'vertical' }>`
    position: relative;
    display: flex;
    flex-direction: ${props => props.$orientation === 'horizontal' ? 'row' : 'column'};
    align-items: center;
    justify-content: center;
    gap: 10px;
    
    /* Order containers based on orientation */
    & > *:nth-child(1) { /* UserContainer */
        order: ${props => props.$orientation === 'horizontal' ? 1 : 3};
    }
    
    & > *:nth-child(2) { /* OutputImageContainer */
        order: ${props => props.$orientation === 'horizontal' ? 2 : 1};
    }
    
    & > *:nth-child(3) { /* Output3DContainer */
        order: ${props => props.$orientation === 'horizontal' ? 3 : 2};
    }
`;

const UserContainer = styled(motion.div)<{ $size: number, $selectedTool: string }>`
    background-color: ${Colors.white};
    width: ${props => props.$size}px;
    height: ${props => props.$size}px;
    border-radius: 4px;
    border: 1px solid ${Colors.black};
    object-fit: contain;
    aspect-ratio: 1/1;
    overflow: hidden;
    position: relative;
    object-fit: contain;
    aspect-ratio: 1/1;
    overflow: hidden;
    cursor: ${props => props.$selectedTool === tools.select ? 'default' : 'crosshair'};
`;

const OutputImageContainer = styled(motion.div)<{ $size: number, $bgColor: string }>`
    // background-color: ${props => props.$bgColor};
    background-color: ${Colors.transparent};
    width: ${props => props.$size}px;
    height: ${props => props.$size}px;
    border-radius: 4px;
    border: 1px solid ${Colors.grey2};
    object-fit: contain;
    aspect-ratio: 1/1;
    overflow: hidden;
    position: relative;
`;

const OutputImage = styled.svg<{ $size: number }>`
    background-color: ${Colors.transparent};
    border-radius: 4px;
    touch-action: none;
    width: ${props => props.$size}px;
    height: ${props => props.$size}px;
    object-fit: contain;
    aspect-ratio: 1/1;
`;

const Output3DContainer = styled(motion.div)<{ $size: number, $bgColor: string }>`
    background-color: ${Colors.transparent};
    width: ${props => props.$size}px;
    height: ${props => props.$size}px;
    border-radius: 4px;
    border: 1px solid ${Colors.grey2};
    object-fit: contain;
    aspect-ratio: 1/1;
    overflow: hidden;
    position: relative;
`;

const LoadingSpinnerContainer = styled.div`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 100px;
    height: 100px;
    padding: 0;
    z-index: 10;
`;

const MiniUserImageContainer = styled(motion.div)<{ $size: number }>`
    position: absolute;
    top: 6px;
    left: 6px;
    width: ${props => props.$size}px;
    height: ${props => props.$size}px;
    border-radius: 4px;
    border: 1px solid ${Colors.grey1};
    background-color: ${Colors.white};
    overflow: hidden;
    z-index: 10;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    cursor: pointer;
`;

export interface ArtStageProps extends KonvaCanvasProps, ArtDescriptionProps {
    artType: ArtType;
    aiImage: string;
    userPng: string; // User's drawing as PNG data URL
    orientation?: 'horizontal' | 'vertical';
    aiPanelSize?: number; // Optional AI panel size for responsive layouts
    onImageAdd: (dataUrl: string, transformState?: TransformState) => void;
    onToolChange: (tool: string) => void;
    isGeneratingImage: boolean;
    isGenerating3D: boolean;
    model3D: string;
    stagePhase: Phases3D;
    onSetStagePhase: (phase: Phases3D) => void;
}

const ArtStage = forwardRef<KonvaArtStageRefs, ArtStageProps>((props, ref) => {
    const outputRef = useRef<SVGSVGElement>(null);
    const MINI_SIZE = window.innerWidth < 768 ? 40 : 80;

    // useEffect(() => {
    //     if (props.model3D) {
    //         console.log('aiModel: ', props.model3D);
    //     }
    // }, [props.model3D]);

    // useEffect(() => {
    //     if (props.stagePhase) {
    //         console.log('stagePhase: ', props.stagePhase);
    //     }
    // }, [props.stagePhase]);
    
    const handleImageAdd = useCallback((dataUrl: string, transformState?: TransformState) => {
        if (props.onImageAdd) {
            props.onImageAdd(dataUrl, transformState);
        }
    }, [props.onImageAdd]);
    
    const handleToolChange = useCallback((tool: string) => {
        if (props.onToolChange) {
            props.onToolChange(tool);
        }
    }, [props.onToolChange]);
    
    // Determine the orientation - use the provided orientation prop or fall back to reflowStage
    const orientation = props.orientation || (props.reflowStage ? 'vertical' : 'horizontal');
    
    return (
        <StageRoot>
            <ArtDescription
                config={props.config}
                artType={props.artType}
                description={props.description}
                onChange={props.onChange}
                onGenerateClick={props.onGenerateClick}
                isGeneratingImage={props.isGeneratingImage}
                hasDrawing={props.hasDrawing}
            />
            <Inner $orientation={orientation}>
                <AnimatePresence>
                    {props.stagePhase === 'image' && (
                        <UserContainer 
                            key="user-image-container"
                            $size={props.imageSize} 
                            $selectedTool={props.selectedTool}
                            initial={{ 
                                x: orientation === 'horizontal' ? -props.imageSize : 0,
                                y: orientation === 'vertical' ? props.imageSize : 0
                            }}
                            animate={{ 
                                x: 0, 
                                y: 0 
                            }}
                            transition={{ 
                                duration: 0.2
                            }}
                        >
                            <KonvaCanvas 
                                ref={ref} 
                                {...props} 
                                onImageAdd={handleImageAdd}
                                onToolChange={handleToolChange}
                            />
                        </UserContainer>
                    )}
                </AnimatePresence>
                
                <OutputImageContainer 
                    id="output-container"
                    $size={props.aiPanelSize || props.imageSize} 
                    $bgColor={props.aiImage ? Colors.transparent : Colors.white}
                    initial={{ 
                        x: orientation === 'horizontal' ? props.aiPanelSize : 0,
                        y: orientation === 'vertical' ? props.aiPanelSize : 0
                    }}
                    animate={{ 
                        x: 0, 
                        y: 0 
                    }}
                    transition={{ 
                        duration: 0.2
                    }}
                >
                    {props.stagePhase === '3d' && props.artType === '3D' && (
                        <MiniUserImageContainer 
                            key="mini-user-image-container"
                            $size={MINI_SIZE}
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.8 }}
                            transition={{ duration: 0.2 }}
                            onClick={() => props.onSetStagePhase('image')}
                        >
                            <OutputImage $size={MINI_SIZE}>
                                <image href={props.userPng} height="100%" width="100%" preserveAspectRatio="xMidYMid meet"/>
                            </OutputImage>
                        </MiniUserImageContainer>
                    )}
                    <OutputImage ref={outputRef} $size={props.aiPanelSize || props.imageSize} >
                        {props.aiImage && <image href={props.aiImage} height="100%" width="100%" preserveAspectRatio="xMidYMid meet"/>}
                    </OutputImage>
                    {props.isGeneratingImage && (
                        <LoadingSpinnerContainer>
                            <LoadingSpinner size="medium" />
                        </LoadingSpinnerContainer>
                    )}
                    {props.stagePhase === 'image' && props.model3D && (
                        <Mini3DView model3D={props.model3D} onClick={() => props.onSetStagePhase('3d')} miniSize={MINI_SIZE} />
                    )}
                </OutputImageContainer>
                
                <AnimatePresence>
                    {props.artType === '3D' && props.stagePhase !== 'image' && (
                        <Output3DContainer 
                            key="3d-container"
                            $size={props.imageSize || props.aiPanelSize} 
                            $bgColor={props.aiImage ? Colors.transparent : Colors.white}
                            initial={{ 
                                x: orientation === 'horizontal' ? -props.imageSize : 0,
                                y: orientation === 'vertical' ? -props.imageSize : 0
                            }}
                            animate={{ 
                                x: 0, 
                                y: 0 
                            }}
                            transition={{ 
                                duration: 0.2
                            }}
                        >
                            {props.model3D && <Stage3D model3D={props.model3D} miniSize={MINI_SIZE} />}
                            {props.isGenerating3D && (
                                <LoadingSpinnerContainer>
                                    <LoadingSpinner size="medium" />
                                </LoadingSpinnerContainer>
                            )}
                        </Output3DContainer>
                    )}
                </AnimatePresence>
            </Inner>
        </StageRoot>
    );
});

export default ArtStage;
